import React, { FC, KeyboardEvent, useCallback } from 'react';

import { Icon, RotateIcon } from '@rbilabs/components-library';
import styled, { css } from 'styled-components';

import { IPickerAspectUiPattern } from '@rbi-ctg/menu';
import { ClickableContainer } from 'components/clickable-container';
import useFocusOnMountRef from 'hooks/use-focus-on-mount-ref';
import { useToggleState } from 'hooks/use-toggle-state';
import { isUiPatternRadio } from 'state/menu/types';
import { isButtonLikeKeypress } from 'utils/event';

import { SelectedOption } from './selected-option';
import SelectionOptions from './selection-options';
import { SelectionOptionsDescription } from './selection-options-description';
import { SelectionOptionsHeader } from './selection-options-header';
import { SelectionOptionsTitle } from './selection-options-title';

const SideContainer = styled.div`
  display: grid;
  grid-template-columns: auto auto;
  grid-gap: 8px;
  align-items: center;
`;

interface ISelectionContent extends IPickerAspectUiPattern {
  isOpen: boolean;
}
const SelectionContent = styled.div<ISelectionContent>`
  overflow: hidden;
  height: ${p => (p.isOpen ? 'auto' : 0)};

  & * {
    visibility: ${p => (p.isOpen ? 'visible' : 'hidden')};
  }

  ${p =>
    isUiPatternRadio(p) &&
    css`
      overflow: visible;
    `}
`;

const HasHeader = styled.div<IPickerAspectUiPattern>`
  ${p =>
    isUiPatternRadio(p) &&
    css`
      display: none;
    `}
`;

interface ISelectionContainerInternalStateProps extends IPickerAspectUiPattern {
  title?: React.ReactNode;
  focusOnMount?: boolean;
  openOnMount?: boolean;
  required?: boolean;
  requirementSatisfied?: boolean;
  children: React.ReactNode;
  selection?: string;
  description?: string;
}

interface ISelectionContainerProps extends ISelectionContainerInternalStateProps {
  optionsVisible: boolean;
  toggleOptionsVisible: () => void;
}

export const SelectionContainer: FC<ISelectionContainerProps> = props => {
  const {
    children,
    focusOnMount,
    optionsVisible,
    selection,
    title,
    toggleOptionsVisible,
    uiPattern,
    description,
  } = props;

  const titleRef = useFocusOnMountRef(!!focusOnMount);

  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (isButtonLikeKeypress(e)) {
        e.preventDefault();
        toggleOptionsVisible();
      }
    },
    [toggleOptionsVisible]
  );

  return (
    <SelectionOptions data-testid={`selection-${title}`} isOpen={optionsVisible}>
      <HasHeader uiPattern={uiPattern}>
        <SelectionOptionsHeader
          aria-controls={`${title}-selection-content`}
          aria-expanded={optionsVisible}
          as={ClickableContainer}
          tabIndex={0}
          onClick={() => toggleOptionsVisible()}
          onKeyDown={onKeyDown}
        >
          <SelectionOptionsTitle ref={titleRef} tabIndex={-1}>
            {title}
          </SelectionOptionsTitle>
          <SideContainer>
            <SelectedOption>{selection}</SelectedOption>
            <RotateIcon active={optionsVisible}>
              <Icon icon="select" color="icon-default" width="24px" aria-hidden />
            </RotateIcon>
          </SideContainer>
        </SelectionOptionsHeader>
      </HasHeader>
      <SelectionContent
        id={`${title}-selection-content`}
        uiPattern={uiPattern}
        isOpen={optionsVisible || isUiPatternRadio(props)}
      >
        {description && <SelectionOptionsDescription>{description}</SelectionOptionsDescription>}
        {children}
      </SelectionContent>
    </SelectionOptions>
  );
};

const SelectionContainerInternalState: FC<ISelectionContainerInternalStateProps> = props => {
  const [optionsVisible, toggleOptionsVisible] = useToggleState(Boolean(props.openOnMount));

  return (
    <SelectionContainer
      optionsVisible={optionsVisible}
      toggleOptionsVisible={toggleOptionsVisible}
      {...props}
    />
  );
};

export default SelectionContainerInternalState;
